import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useData } from '../../contexts';
import { active } from 'd3';

interface DataContext {
  drupalData: Queries.paragraph__reports;
  additionalData: {
    allCities: Queries.City[];
  };
}

interface Benchmark {
  type: string;
  optionLabel: string;
  label: string;
  report: {
    pdf: string;
    word: string;
  };
  report_advanced: {
    pdf: string;
    word: string;
  };
}

export default function Reports({
  drupalData,
  additionalData,
}: DataContext): React.JSX.Element {
  const { selectedGemeente, setSelectedGemeente } = useData();
  let defaultCity =
    selectedGemeente.id !== -1
      ? additionalData.allCities.find(
          (city: Queries.City) => city.data.ruimte.id === selectedGemeente.id,
        )
      : additionalData.allCities[0];

  defaultCity = defaultCity ? defaultCity : additionalData.allCities[0];

  const cities = additionalData.allCities.map(
    (city: Queries.City) => city.name,
  );
  const [queryParams, setQueryParams] = useSearchParams({
    gemeente: defaultCity.name,
  });

  let activeCity = queryParams.get('gemeente');

  const activeCityValid = additionalData.allCities.find(
    (city: Queries.City) => city.name === activeCity,
  );

  activeCity = activeCityValid ? activeCity : defaultCity.name;

  const activeBenchmark = queryParams.get('benchmark') ?? 'flemish_region';

  const benchmarks = additionalData.allCities.find(
    (c: Queries.City) => c.name === activeCity,
  ).benchmarkReports;
  const localBenchmark = additionalData.allCities.find(
    (c: Queries.City) => c.name === activeCity,
  ).localReport;
  const selectedBenchmark = benchmarks.find(
    (b: Benchmark) => b.type === activeBenchmark,
  );

  const onCityChange = function (event): void {
    event.preventDefault();

    const c = event.target.value;
    const targetedCity = additionalData.allCities.find(
      (city: Queries.City) => city.name === c,
    );
    const cbecnhmarks = targetedCity.benchmarkReports;
    queryParams.set('gemeente', c);
    if (
      typeof cbecnhmarks.find((b: Benchmark) => b.type === activeBenchmark) ===
      'undefined'
    ) {
      queryParams.set('benchmark', cbecnhmarks[0].type);
    }

    targetedCity.data.ruimte.label = targetedCity.data.ruimte.ruimte;

    setSelectedGemeente(targetedCity.data.ruimte);
    setQueryParams(queryParams);
  };

  const onBenchmarkChange = function (event): void {
    queryParams.set('benchmark', event.target.value);
    setQueryParams(queryParams);
  };

  const downloads = selectedBenchmark ? (
    <div
      className="vl-u-highlight-content vl-u-spacer-top--medium vl-u-spacer-bottom--medium"
      style={{ backgroundColor: '#fff', border: '0' }}
    >
      <span className="vl-annotation">{selectedBenchmark.label}</span>
      <ul className="vl-icon-list vl-u-spacer-top--small">
        <li className="vl-icon-list__item">
          <i className="vl-vi vl-vi-download-harddisk" aria-hidden="true" />
          <span>Uitgebreid rapport</span>&nbsp;(
          <a
            href={selectedBenchmark.report_advanced.pdf}
            className="vl-link"
            target="_blank"
            rel="noreferrer"
          >
            PDF
          </a>
          &nbsp;/&nbsp;
          <a
            href={selectedBenchmark.report_advanced.word}
            className="vl-link"
            target="_blank"
            rel="noreferrer"
          >
            Word
          </a>
          )
        </li>
        <li className="vl-icon-list__item">
          <i className="vl-vi vl-vi-download-harddisk" aria-hidden="true" />
          <span>Samenvattend rapport</span>&nbsp;(
          <a
            href={selectedBenchmark.report.pdf}
            className="vl-link"
            target="_blank"
            rel="noreferrer"
          >
            PDF
          </a>
          &nbsp;/&nbsp;
          <a
            href={selectedBenchmark.report.word}
            className="vl-link"
            target="_blank"
            rel="noreferrer"
          >
            Word
          </a>
          )
        </li>
      </ul>
    </div>
  ) : (
    <></>
  );

  const localDownloads = activeCity ? (
    <div
      className="vl-u-highlight-content vl-u-spacer-top--medium vl-u-spacer-bottom--medium"
      style={{ backgroundColor: '#fff', border: '0' }}
    >
      <span className="vl-annotation">Jouw lokale integratiescan</span>
      <div className="vl-u-spacer-top--small">
        <ul className="vl-icon-list">
          <li className="vl-icon-list__item">
            <i className="vl-vi vl-vi-download-harddisk" aria-hidden="true" />
            <span>Uitgebreid rapport</span>&nbsp;(
            <a
              href={localBenchmark.report.pdf}
              className="vl-link"
              target="_blank"
              rel="noreferrer"
            >
              PDF
            </a>
            &nbsp;/&nbsp;
            <a
              href={localBenchmark.report.word}
              className="vl-link"
              target="_blank"
              rel="noreferrer"
            >
              Word
            </a>
            )
          </li>
        </ul>
      </div>
    </div>
  ) : (
    <></>
  );

  return (
    <section className="vl-region">
      <div className="vl-layout">
        <div className="vl-grid vl-grid--is-stacked">
          <div className="vl-col--12-12">
            <div className="vl-u-bg-alt" style={{ padding: '2.4rem' }}>
              <h2 className="vl-title vl-title--h2">Jouw gemeente</h2>
              <div className="vl-form-grid vl-form-grid--is-stacked">
                <div className="vl-form-col--8-12 vl-form-col--12-12--s">
                  <select
                    name="city"
                    id="city"
                    className="vl-select vl-select--block"
                    tabIndex={0}
                    data-vl-select=""
                    data-vl-select-search-empty-text="Geen resultaten gevonden"
                    value={activeCity ?? 'none'}
                    onChange={onCityChange}
                  >
                    {cities.map((city: string, idx: number) => (
                      <option key={idx} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div
            className={
              'vl-col--6-12 vl-col--12-12--m ' +
              (activeBenchmark && activeCity ? '' : 'vl-u-hidden')
            }
          >
            <div className="vl-u-bg-alt" style={{ padding: '2.4rem' }}>
              <h2 className="vl-title vl-title--h2">Jouw gemeentescan</h2>
              <span className="vl-annotation">Benchmark</span>
              {benchmarks.map((benchmark: Benchmark) => (
                <label
                  key={benchmark.type}
                  className="vl-radio vl-radio--block vl-u-spacer-top--xsmall"
                  htmlFor={`benchmark-${benchmark.type}`}
                >
                  <input
                    className="vl-radio__toggle"
                    type="radio"
                    id={`benchmark-${benchmark.type}`}
                    name="benchmark"
                    defaultValue={benchmark.type}
                    checked={activeBenchmark === benchmark.type}
                    onChange={onBenchmarkChange}
                  />
                  <div className="vl-radio__label">{benchmark.optionLabel}</div>
                </label>
              ))}

              {downloads}

              <div className="vl-u-spacer-top--medium">
                <span
                  className="vl-annotation"
                  style={{ display: 'inline-block', 'margin-bottom': '1rem' }}
                >
                  Benchmark naar keuze
                </span>
                <div
                  className="vl-typography"
                  dangerouslySetInnerHTML={{
                    __html: drupalData.field_extra_city_scan.processed,
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className={
              'vl-col--6-12 vl-col--12-12--m ' +
              (activeBenchmark && activeCity ? '' : 'vl-u-hidden')
            }
          >
            <div className="vl-u-bg-alt" style={{ padding: '2.4rem' }}>
              <h2 className="vl-title vl-title--h2">
                Jouw lokale integratiescan
              </h2>
              <p>
                Voor ‘Jouw lokale integratiescan’ is enkel een uitgebreid
                rapport beschikbaar.
              </p>
              {localDownloads}
              <div
                className="vl-typography"
                dangerouslySetInnerHTML={{
                  __html: drupalData.field_extra_local_scan.processed,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
